export const SELECTION_STORAGE_KEY = 'lups-color-selector-preview:v1';
const MAX_SAFE_QUANTITY = Number.MAX_SAFE_INTEGER;
const DEFAULT_LABELS = Object.freeze({
  "individual": "Individual",
  "duo": "Pack Dúo",
  "travel": "Pack Travel",
  "four": "4 LUPS",
  "five": "5+ LUPS",
  "pending": "Por confirmar",
  "empty": "Elige tus colores",
  "unavailable": "Agotado",
  "selected": "Seleccionada",
  "select": "Seleccionar",
  "deselect": "Quitar",
  "decrease": "Quitar una funda",
  "increase": "Añadir otra funda",
  "quantity": "Cantidad",
  "shipping": "Envío gratis",
  "shippingFirst": "Envío gratis desde {threshold}",
  "progressZero": "Empieza por tu color favorito.",
  "progressOne": "Añade una más: 2 fundas por {total}{shippingSuffix}.",
  "progressTwo": "Una más por {difference}: 3 fundas por {total}{shippingSuffix}.",
  "progressThree": "Una más por {difference}: 4 fundas por {total}{shippingSuffix}.",
  "progressFour": "Una más por {difference}: 5 fundas por {total}{shippingSuffix}.",
  "progressFive": "Desde 5 fundas mantienes el mismo precio por funda. Añade todas las que necesites.",
  "limit": "No se pueden añadir más unidades a esta selección.",
  "stock": "No quedan más unidades disponibles de este color.",
  "invalid": "No se ha podido cargar este color.",
  "emptySelection": "Todavía no has elegido ningún color.",
  "unit": "funda",
  "units": "fundas",
  "previewTitle": "Así quedaría tu selección",
  "previewNote": "Prueba de diseño: no se ha creado ningún pedido ni modificado tu carrito.",
  "close": "Seguir eligiendo",
  "clear": "Borrar selección",
  "total": "Total",
  "savings": "Ahorras",
  "pendingNote": "El precio de esta cantidad está pendiente de confirmar.",
  "error": "No se han podido cargar los colores. Recarga la página para volver a intentarlo.",
  "shippingMainland": "Envío gratis en península",
  "shippingFirstMainland": "Envío gratis en península desde {threshold}",
  "shippingPaid": "Envío: {fee}. {rule}",
  "shippingForeign": "{rule}. Conversión y coste final de envío por confirmar al pagar.",
  "shippingUnknown": "Envío por confirmar para este destino.",
  "shippingIneligible": "Condiciones de envío por confirmar para este destino.",
  "shippingProposal": "{rule}",
  "progressPending": "El precio de la siguiente cantidad está por confirmar.",
  "progressShipping": " y envío gratis",
  "progressShippingMainland": " y envío gratis en península",
  "caseSelected": "funda seleccionada",
  "casesSelected": "fundas seleccionadas",
  "selectionAdjusted": "Tu selección se ha ajustado a los colores y unidades disponibles para este destino.",
  "from": "Desde"
  ,"quoteLoading": "Calculando tu precio…"
  ,"quoteError": "No hemos podido confirmar el precio. Vuelve a intentarlo en unos instantes."
  ,"quoteRetry": "Volver a calcular"
  ,"progressAutomatic": "El total se actualiza según las fundas que elijas."
  ,"fiveExample": "5 fundas: {total}"
});

function validAmount(value) {
  return Number.isSafeInteger(value) && value >= 0;
}

function validCurrency(value) {
  return typeof value === 'string' && /^[A-Z]{3}$/.test(value);
}

function availableStock(item) {
  if (item.available === false) return 0;
  if (item.stock === null || item.stock === undefined || item.stock === '') return MAX_SAFE_QUANTITY;
  if (!['number', 'string'].includes(typeof item.stock)) return 0;
  const value = Number(item.stock);
  return Number.isFinite(value) ? Math.max(0, Math.min(MAX_SAFE_QUANTITY, Math.floor(value))) : 0;
}

function selectedEntries(quantities) {
  if (!quantities || typeof quantities !== 'object' || Array.isArray(quantities)) return [];
  return Object.entries(quantities).filter(([, quantity]) => Number.isSafeInteger(quantity) && quantity > 0);
}

function selectedCount(entries) {
  let count = 0;
  for (const [, quantity] of entries) {
    if (quantity > MAX_SAFE_QUANTITY - count) return null;
    count += quantity;
  }
  return count;
}

function safeAmountFromBigInt(amount) {
  return amount <= BigInt(Number.MAX_SAFE_INTEGER) ? Number(amount) : null;
}

function currencyRoundingIncrement(market) {
  try {
    const digits = new Intl.NumberFormat(market.locale, { style: 'currency', currency: market.currency }).resolvedOptions().maximumFractionDigits;
    const divisor = Number.isSafeInteger(market.amountDivisor) && market.amountDivisor > 0 ? market.amountDivisor : 100;
    return Math.max(1, Math.round(divisor / (10 ** digits)));
  } catch { return 1; }
}

export function priceForQuantity(count, market = {}) {
  if (!Number.isSafeInteger(count) || count < 0) return null;
  if (count === 0) return 0;
  const tier = Math.min(count, 5);
  const price = market.prices?.[tier];
  if (!validCurrency(market.currency) || !validAmount(price) || market.pendingTiers?.includes(tier)) return null;
  if (count <= 5) return price;
  const increment = BigInt(currencyRoundingIncrement(market));
  const denominator = 5n * increment;
  return safeAmountFromBigInt(((BigInt(price) * BigInt(count) + denominator / 2n) / denominator) * increment);
}

export function normalizeMarket(source = {}) {
  if (!source || typeof source !== 'object') source = {};
  const currency = validCurrency(source.currency) ? source.currency : null;
  let locale = typeof source.locale === 'string' ? source.locale : (globalThis.document?.documentElement?.lang || 'es');
  try { locale = Intl.getCanonicalLocales(locale)[0] || 'es'; } catch { locale = 'es'; }
  const pendingTiers = Array.isArray(source.pendingTiers) ? source.pendingTiers.filter((count) => Number.isSafeInteger(count) && count > 0 && count <= 5) : [];
  const prices = { 0: 0 };
  for (let count = 1; count <= 5; count += 1) {
    const value = source.prices?.[count];
    prices[count] = currency && validAmount(value) && !pendingTiers.includes(count) ? value : null;
  }
  const shipping = source.shipping && typeof source.shipping === 'object' ? source.shipping : {};
  return {
    country: typeof source.country === 'string' && /^[A-Z]{2}$/.test(source.country) ? source.country : null,
    currency,
    locale,
    amountDivisor: Number.isSafeInteger(source.amountDivisor) && source.amountDivisor > 0 ? source.amountDivisor : 100,
    unitPrice: currency && validAmount(source.unitPrice) ? source.unitPrice : null,
    prices,
    priceSource: typeof source.priceSource === 'string' ? source.priceSource : 'unavailable',
    pendingTiers,
    shipping: {
      eligible: typeof shipping.eligible === 'boolean' ? shipping.eligible : null,
      region: ['mainland_es', 'country'].includes(shipping.region) ? shipping.region : 'unknown',
      mode: ['proposal', 'configured'].includes(shipping.mode) ? shipping.mode : 'unknown',
      paidFee: validAmount(shipping.paidFee) ? shipping.paidFee : null,
      paidCurrency: validCurrency(shipping.paidCurrency) ? shipping.paidCurrency : null,
      freeThreshold: validAmount(shipping.freeThreshold) ? shipping.freeThreshold : null,
      freeCurrency: validCurrency(shipping.freeCurrency) ? shipping.freeCurrency : null,
      currentFreeThreshold: validAmount(shipping.currentFreeThreshold) ? shipping.currentFreeThreshold : null,
      requiresProvince: shipping.requiresProvince === true,
      note: typeof shipping.note === 'string' ? shipping.note : ''
    }
  };
}

export function calculateShipping(total, market = {}) {
  const shipping = market.shipping || {};
  if (shipping.eligible === false) return { shippingFree: false, status: 'ineligible' };
  if (shipping.eligible !== true || shipping.region === 'unknown' || shipping.mode === 'unknown' || !validAmount(shipping.freeThreshold)) return { shippingFree: null, status: 'pending' };
  if (!validCurrency(market.currency) || !validCurrency(shipping.freeCurrency)) return { shippingFree: null, status: 'pending' };
  if (shipping.freeCurrency !== market.currency) return { shippingFree: null, status: 'foreign_currency' };
  if (!validAmount(total)) return { shippingFree: null, status: 'pending_total' };
  const shippingFree = total >= shipping.freeThreshold;
  return { shippingFree, status: shippingFree ? 'free' : 'paid' };
}

export function calculateSelection(quantities, market = {}, catalog = []) {
  const selected = selectedEntries(quantities);
  const count = selectedCount(selected);
  let total = priceForQuantity(count, market);
  let original = count === 0 ? 0 : count !== null && validAmount(market.unitPrice)
    ? safeAmountFromBigInt(BigInt(count) * BigInt(market.unitPrice)) : null;
  if (catalog.length) {
    const selectedPricesKnown = selected.every(([id]) => validAmount(catalog.find((item) => String(item.id) === id)?.price));
    original = count !== null && selectedPricesKnown ? safeAmountFromBigInt(selected.reduce((sum, [id, quantity]) => sum + BigInt(quantity) * BigInt(catalog.find((item) => String(item.id) === id).price), 0n)) : null;
    if (count === 1) total = validCurrency(market.currency) && original !== null ? original : null;
  }
  const shipping = calculateShipping(count === 0 ? null : total, market);
  return { count, total, original, retailBase: original, savings: total === null || original === null ? null : Math.max(0, original - total), shippingFree: shipping.shippingFree, shipping };
}

export function quoteMatchesSelection(quote, quantities, market) {
  if (quote?.status !== 'ready' || quote.country !== market.country || quote.currency !== market.currency || !validAmount(quote.total)) return false;
  const entries = selectedEntries(quantities);
  const quoted = selectedEntries(quote.quantities);
  return quote.quantities && Object.keys(quote.quantities).length === quoted.length && entries.length === quoted.length && entries.every(([id, quantity]) => quote.quantities[id] === quantity);
}

export function calculateDisplaySelection(quantities, market, catalog, quote, requireServerQuote = false) {
  const state = calculateSelection(quantities, market, catalog);
  if (!requireServerQuote || state.count === 0) return { ...state, quoteReady: !requireServerQuote || state.count === 0 };
  const ready = quoteMatchesSelection(quote, quantities, market);
  const total = ready ? quote.total : null;
  const shipping = calculateShipping(total, market);
  return { ...state, total, savings: total === null || state.original === null ? null : Math.max(0, state.original - total), shipping, shippingFree: shipping.shippingFree, quoteReady: ready };
}

export function getIndividualOfferPrice(quantities, market, catalog) {
  const state = calculateSelection(quantities, market, catalog);
  if (state.count === 1) return state.total;
  if (!validCurrency(market.currency)) return null;
  const prices = catalog.filter((item) => availableStock(item) > 0 && validAmount(item.price)).map((item) => item.price);
  return prices.length ? Math.min(...prices) : null;
}

export function hasVariableUnitPrices(catalog) {
  return new Set(catalog.filter((item) => availableStock(item) > 0 && validAmount(item.price)).map((item) => item.price)).size > 1;
}

export function sanitizeSelection(catalog, current, maxTotal = MAX_SAFE_QUANTITY) {
  if (!current || typeof current !== 'object' || Array.isArray(current)) return {};
  const quantities = {};
  let remaining = Number.isSafeInteger(maxTotal) && maxTotal >= 0 ? maxTotal : MAX_SAFE_QUANTITY;
  for (const [id, quantity] of Object.entries(current)) {
    if (!Number.isSafeInteger(quantity) || quantity <= 0 || remaining <= 0) continue;
    const item = catalog.find((color) => String(color.id) === id);
    if (!item) continue;
    const allowed = Math.min(quantity, availableStock(item), remaining);
    if (allowed > 0) quantities[id] = allowed;
    remaining -= allowed;
  }
  return quantities;
}

export function restoreSelection(catalog, storage) {
  try {
    const saved = JSON.parse(storage?.getItem(SELECTION_STORAGE_KEY) || 'null');
    if (saved?.version !== 1) return {};
    return sanitizeSelection(catalog, saved.quantities);
  } catch { return {}; }
}

export function persistSelection(quantities, storage) {
  try {
    if (!storage) return false;
    storage.setItem(SELECTION_STORAGE_KEY, JSON.stringify({ version: 1, quantities }));
    return true;
  } catch { return false; }
}

export function transitionSelection(catalog, current, id, action, maxTotal = MAX_SAFE_QUANTITY) {
  const key = String(id);
  const item = catalog.find((color) => String(color.id) === key);
  const quantities = sanitizeSelection(catalog, current, maxTotal);
  const existing = Number.isSafeInteger(quantities[key]) && quantities[key] > 0 ? quantities[key] : 0;
  if (!item) return { quantities, changed: false, reason: 'invalid' };
  if (action === 'toggle' && existing > 0) {
    delete quantities[key];
    return { quantities, changed: true, reason: null };
  }
  if (action === 'decrease') {
    if (!existing) return { quantities, changed: false, reason: null };
    if (existing === 1) delete quantities[key];
    else quantities[key] = existing - 1;
    return { quantities, changed: true, reason: null };
  }
  if (action !== 'toggle' && action !== 'increase') return { quantities, changed: false, reason: 'invalid' };
  const stock = availableStock(item);
  if (existing >= stock) return { quantities, changed: false, reason: 'stock' };
  const limit = Number.isSafeInteger(maxTotal) && maxTotal >= 0 ? maxTotal : MAX_SAFE_QUANTITY;
  if (selectedCount(selectedEntries(quantities)) >= limit) return { quantities, changed: false, reason: 'limit' };
  quantities[key] = existing + 1;
  return { quantities, changed: true, reason: null };
}

const ElementBase = globalThis.HTMLElement || class {};

export class LupsColorSelector extends ElementBase {
  connectedCallback() {
    if (this.initialized) {
      this.connectCountryOutsideListener();
      return;
    }
    this.initialized = true;
    this.quantities = {};
    this.cards = new Map();
    this.labels = { ...DEFAULT_LABELS };
    const labelScript = this.querySelector('[data-selector-labels]');
    if (labelScript) {
      try { Object.assign(this.labels, JSON.parse(labelScript.textContent)); } catch {}
    }
    try { this.market = normalizeMarket(JSON.parse(this.querySelector('[data-selector-market]')?.textContent || '{}')); }
    catch { this.market = normalizeMarket(); }
    try { this.requireServerQuote = JSON.parse(this.querySelector('[data-selector-quote-config]')?.textContent || '{}').enabled === true; }
    catch { this.requireServerQuote = false; }
    this.addEventListener('lups:quote-result', (event) => this.applyServerQuote(event.detail));
    this.addEventListener('lups:quote-offers', (event) => this.applyServerOffers(event.detail));
    try { this.storage = globalThis.sessionStorage; } catch { this.storage = null; }
    try {
      const rawCatalog = JSON.parse(this.querySelector('[data-selector-catalog]')?.textContent || '[]');
      if (!Array.isArray(rawCatalog) || !rawCatalog.length) throw new Error('catalog');
      const ids = new Set();
      this.catalog = rawCatalog.filter((item) => {
        if (!item || item.id === undefined || !item.title || ids.has(String(item.id))) return false;
        ids.add(String(item.id));
        return true;
      });
      if (!this.catalog.length) throw new Error('catalog');
      this.quantities = restoreSelection(this.catalog, this.storage);
      this.createGrid();
      this.createDialog();
      this.querySelectorAll('[data-preview-viewport]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.previewViewport === 'desktop'));
        button.addEventListener('click', () => {
          const mobile = button.dataset.previewViewport === 'mobile';
          this.classList.toggle('is-mobile-preview', mobile);
          this.querySelectorAll('[data-preview-viewport]').forEach((control) => {
            control.setAttribute('aria-pressed', String(control.dataset.previewViewport === (mobile ? 'mobile' : 'desktop')));
          });
        });
      });
      this.querySelector('[data-clear-selection]')?.addEventListener('click', () => this.clearSelection());
      this.querySelector('[data-preview-checkout]')?.addEventListener('click', () => this.openPreview());
      this.querySelectorAll('[data-selector-country-form]').forEach((form) => this.setupCountryForm(form));
      this.connectCountryOutsideListener();
      this.renderSummary();
    } catch {
      const grid = this.querySelector('[data-color-grid]');
      if (grid) grid.textContent = this.labels.error;
      const button = this.querySelector('[data-preview-checkout]');
      if (button) button.disabled = true;
    }
  }

  disconnectedCallback() {
    if (this.countryOutsideListener) this.ownerDocument.removeEventListener('pointerdown', this.countryOutsideListener);
    this.countryOutsideConnected = false;
  }

  submitCountryForm(form) {
    persistSelection(this.quantities, this.storage);
    if (form && typeof form.submit === 'function') {
      form.submit();
      return true;
    }
    return false;
  }

  setupCountryForm(form) {
    form.addEventListener('submit', () => persistSelection(this.quantities, this.storage));
    form.querySelector('[data-market-country]')?.addEventListener('change', () => this.submitCountryForm(form));
    this.setupCountryChooser(form);
  }

  setupCountryChooser(form) {
    const select = form.querySelector('[data-market-country]');
    const toggle = form.querySelector('[data-country-toggle]');
    const list = form.querySelector('[data-country-list]');
    if (!select || !toggle || !list) return;
    const options = Array.from(list.querySelectorAll('[data-country-option]')).filter((option) => !option.disabled && option.getAttribute('aria-disabled') !== 'true');
    if (!options.length) return;
    const focusOption = (index) => {
      const current = (index + options.length) % options.length;
      options.forEach((option, position) => { option.tabIndex = position === current ? 0 : -1; });
      options[current].focus();
    };
    const selectedIndex = () => Math.max(0, options.findIndex((option) => option.dataset.countryCode === select.value));
    const close = (returnFocus = false) => {
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      options.forEach((option) => { option.tabIndex = -1; });
      if (returnFocus) toggle.focus();
    };
    const open = () => {
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      focusOption(selectedIndex());
    };
    options.forEach((option) => {
      option.tabIndex = -1;
      option.setAttribute('aria-selected', String(option.dataset.countryCode === select.value));
      option.addEventListener('click', () => {
        const code = option.dataset.countryCode;
        if (!code || !Array.from(select.options).some((nativeOption) => nativeOption.value === code)) return;
        const changed = code !== select.value;
        select.value = code;
        options.forEach((item) => { item.setAttribute('aria-selected', String(item === option)); });
        const name = option.querySelector('[data-country-option-name]')?.textContent?.trim()
          || Array.from(select.options).find((nativeOption) => nativeOption.value === code)?.textContent?.trim()
          || code;
        const currentName = form.querySelector('[data-country-current-name]');
        if (currentName) currentName.textContent = name;
        const flag = option.querySelector('img');
        const currentFlag = form.querySelector('[data-country-current-flag]');
        if (flag && currentFlag) currentFlag.src = flag.currentSrc || flag.src;
        close(!changed);
        if (changed) this.submitCountryForm(form);
      });
    });
    toggle.addEventListener('click', () => { if (list.hidden) open(); else close(); });
    toggle.addEventListener('keydown', (event) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        open();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      }
    });
    list.addEventListener('keydown', (event) => {
      const current = options.indexOf(event.target.closest('[data-country-option]'));
      if (event.key === 'Escape') {
        event.preventDefault();
        close(true);
      } else if (event.key === 'Tab') close();
      else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault();
        if (event.key === 'Home') focusOption(0);
        else if (event.key === 'End') focusOption(options.length - 1);
        else focusOption((current < 0 ? selectedIndex() : current) + (event.key === 'ArrowDown' ? 1 : -1));
      }
    });
    close();
    const custom = form.querySelector('[data-country-custom]');
    if (custom) {
      select.hidden = true;
      custom.hidden = false;
    }
    this.countryChoosers ||= [];
    this.countryChoosers.push({ form, close });
  }

  connectCountryOutsideListener() {
    if (this.countryOutsideConnected || !this.countryChoosers?.length) return;
    this.countryOutsideListener ||= (event) => {
      this.countryChoosers.forEach(({ form, close }) => { if (!form.contains(event.target)) close(); });
    };
    this.ownerDocument.addEventListener('pointerdown', this.countryOutsideListener);
    this.countryOutsideConnected = true;
  }

  formatMoney(amount, currency = this.market?.currency) {
    if (!Number.isFinite(amount) || !validCurrency(currency)) return this.labels.pending;
    try {
      return new Intl.NumberFormat(this.market.locale, { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount / this.market.amountDivisor);
    } catch { return this.labels.pending; }
  }

  interpolate(template, values) {
    return String(template || '').replace(/\{([a-zA-Z]+)\}/g, (match, key) => values[key] === undefined ? match : String(values[key]));
  }

  getShippingText(state) {
    const shipping = this.market.shipping;
    const mainland = shipping.region === 'mainland_es';
    const threshold = validAmount(shipping.freeThreshold) ? this.formatMoney(shipping.freeThreshold, shipping.freeCurrency) : this.labels.pending;
    const rule = this.interpolate(mainland ? this.labels.shippingFirstMainland : this.labels.shippingFirst, { threshold });
    let text;
    if (state.shipping.status === 'ineligible') text = this.labels.shippingIneligible;
    else if (state.shipping.status === 'pending') text = this.labels.shippingUnknown;
    else if (state.shipping.status === 'foreign_currency') text = this.interpolate(this.labels.shippingForeign, { rule });
    else if (state.shippingFree) text = mainland ? this.labels.shippingMainland : this.labels.shipping;
    else if (validAmount(shipping.paidFee) && validCurrency(shipping.paidCurrency)) text = this.interpolate(this.labels.shippingPaid, { fee: this.formatMoney(shipping.paidFee, shipping.paidCurrency), rule });
    else text = rule;
    if (shipping.mode === 'proposal' && !['pending', 'ineligible'].includes(state.shipping.status)) text = this.interpolate(this.labels.shippingProposal, { rule: text });
    return [text, shipping.note].filter(Boolean).join(' ');
  }

  getProgressText(state) {
    if (state.count === 0) return this.labels.progressZero;
    if (state.count >= 5) return validAmount(state.total) ? this.labels.progressFive : this.labels.progressPending;
    const nextTotal = priceForQuantity(state.count + 1, this.market);
    if (!validAmount(nextTotal) || !validAmount(state.total)) return this.labels.progressPending;
    const nextShipping = calculateShipping(nextTotal, this.market);
    const shippingSuffix = nextShipping.shippingFree === true && state.shippingFree !== true
      ? this.market.shipping.region === 'mainland_es' ? this.labels.progressShippingMainland : this.labels.progressShipping
      : '';
    const template = this.labels[['progressZero', 'progressOne', 'progressTwo', 'progressThree', 'progressFour'][state.count]];
    return this.interpolate(template, { total: this.formatMoney(nextTotal), difference: this.formatMoney(Math.max(0, nextTotal - state.total)), shippingSuffix });
  }

  setText(selector, value) {
    this.querySelectorAll(selector).forEach((node) => { node.textContent = value; });
  }

  createGrid() {
    const grid = this.querySelector('[data-color-grid]');
    if (!grid) throw new Error('grid');
    const fragment = document.createDocumentFragment();
    const existingCards = new Map(Array.from(grid.querySelectorAll('[data-color-id]'), (card) => [String(card.dataset.colorId), card]));
    this.catalog.forEach((item, index) => {
      const id = String(item.id);
      const existingCard = existingCards.get(id);
      if (existingCard) {
        const nodes = {
          card: existingCard,
          photo: existingCard.querySelector('[data-color-toggle]'),
          count: existingCard.querySelector('.lups-color-quantity-count'),
          minus: existingCard.querySelector('[data-color-decrease]'),
          plus: existingCard.querySelector('[data-color-increase]'),
          status: existingCard.querySelector('.lups-color-status')
        };
        if (nodes.photo && nodes.count && nodes.minus && nodes.plus && nodes.status) {
          nodes.photo.addEventListener('click', () => this.changeColor(id, 'toggle'));
          nodes.minus.addEventListener('click', () => this.changeColor(id, 'decrease'));
          nodes.plus.addEventListener('click', () => this.changeColor(id, 'increase'));
          const image = nodes.photo.querySelector('img');
          if (image) image.fetchPriority = index === 0 ? 'high' : 'auto';
          this.cards.set(id, nodes);
          return;
        }
        existingCard.remove();
      }
      const card = document.createElement('article');
      card.className = 'lups-color-card';
      card.dataset.colorId = id;
      const photo = document.createElement('button');
      photo.type = 'button';
      photo.className = 'lups-color-photo';
      photo.dataset.colorToggle = id;
      photo.setAttribute('aria-pressed', 'false');
      const image = document.createElement('img');
      image.src = item.image || '';
      image.alt = item.title;
      image.width = 400;
      image.height = 400;
      image.loading = index < 4 ? 'eager' : 'lazy';
      image.fetchPriority = index === 0 ? 'high' : 'auto';
      image.decoding = 'async';
      const tick = document.createElement('span');
      tick.className = 'lups-color-tick';
      tick.textContent = '✓';
      tick.setAttribute('aria-hidden', 'true');
      photo.append(image, tick);
      photo.addEventListener('click', () => this.changeColor(id, 'toggle'));
      const bottom = document.createElement('div');
      bottom.className = 'lups-color-card-bottom';
      const name = document.createElement('h3');
      name.className = 'lups-color-name';
      name.textContent = item.title;
      const quantity = document.createElement('div');
      quantity.className = 'lups-color-quantity';
      quantity.setAttribute('role', 'group');
      quantity.setAttribute('aria-label', `${this.labels.quantity}: ${item.title}`);
      const minus = document.createElement('button');
      minus.type = 'button';
      minus.textContent = '−';
      minus.setAttribute('aria-label', `${this.labels.decrease}: ${item.title}`);
      minus.dataset.colorDecrease = id;
      minus.addEventListener('click', () => this.changeColor(id, 'decrease'));
      const count = document.createElement('span');
      count.className = 'lups-color-quantity-count';
      count.textContent = '0';
      const plus = document.createElement('button');
      plus.type = 'button';
      plus.textContent = '+';
      plus.setAttribute('aria-label', `${this.labels.increase}: ${item.title}`);
      plus.dataset.colorIncrease = id;
      plus.addEventListener('click', () => this.changeColor(id, 'increase'));
      quantity.append(minus, count, plus);
      const status = document.createElement('span');
      status.className = 'lups-color-status';
      status.hidden = true;
      bottom.append(name, quantity, status);
      card.append(photo, bottom);
      this.cards.set(id, { card, photo, count, minus, plus, status });
      fragment.append(card);
    });
    grid.append(fragment);
    this.message = this.querySelector('[data-selector-message]');
    if (!this.message) {
      this.message = document.createElement('p');
      this.message.dataset.selectorMessage = '';
      this.message.className = 'lups-selector-message';
      this.message.setAttribute('role', 'status');
      this.message.setAttribute('aria-live', 'polite');
      grid.after(this.message);
    }
  }

  changeColor(id, action) {
    if (this.isCartBusy()) return;
    const result = transitionSelection(this.catalog, this.quantities, id, action);
    this.quantities = result.quantities;
    this.message.textContent = result.reason ? this.labels[result.reason] : '';
    this.renderSummary();
  }

  isCartBusy() {
    return this.querySelector('lups-selector-cart')?.getAttribute('aria-busy') === 'true';
  }

  clearSelection({ fromCart = false } = {}) {
    if (this.isCartBusy() && !fromCart) return;
    this.quantities = {};
    if (this.message) this.message.textContent = '';
    this.renderSummary();
  }

  applyServerQuote(quote) {
    if (!quote || quote.country !== this.market.country || quote.currency !== this.market.currency) return;
    const selected = selectedEntries(this.quantities);
    if (!quote.quantities || Object.keys(quote.quantities).length !== selected.length || !selected.every(([id, quantity]) => quote.quantities[id] === quantity)) return;
    this.serverQuote = quote;
    this.renderSummary();
  }

  applyServerOffers(offers) {
    if (!offers || offers.country !== this.market.country || offers.currency !== this.market.currency) return;
    this.serverOffersStatus = offers.status;
    if (offers.status !== 'ready') { this.renderSummary(); return; }
    const prices = { 0: 0 };
    for (let count = 1; count <= 5; count += 1) prices[count] = validAmount(offers.prices?.[count]) ? offers.prices[count] : null;
    this.market.prices = prices;
    this.market.priceSource = 'shopify_storefront';
    this.hasServerOffers = true;
    this.serverOffersMarkFrom = offers.markFrom === true;
    this.renderSummary();
  }

  displaySelection() {
    return calculateDisplaySelection(this.quantities, this.market, this.catalog, this.serverQuote, this.requireServerQuote);
  }

  renderSummary() {
    const state = calculateDisplaySelection(this.quantities, this.market, this.catalog, this.serverQuote, this.requireServerQuote);
    const selectionBar = this.querySelector('.lups-selection-bar');
    if (selectionBar) selectionBar.hidden = state.count === 0;
    const cartBusy = this.isCartBusy();
    persistSelection(this.quantities, this.storage);
    const tierLabels = [this.labels.empty, this.labels.individual, this.labels.duo, this.labels.travel, this.labels.four, this.labels.five];
    this.setText('[data-selected-count], [data-demo-cart-count]', String(state.count));
    this.setText('[data-selected-unit], [data-demo-count-unit]', state.count === 1 ? this.labels.caseSelected : this.labels.casesSelected);
    this.setText('[data-total], [data-demo-cart-total]', state.total === null ? this.labels.pending : this.formatMoney(state.total));
    this.setText('[data-tier-indicator]', state.count >= 5 ? this.labels.five : tierLabels[state.count] || this.labels.pending);
    this.querySelectorAll('[data-offer-tier]').forEach((tile) => {
      const tier = Number(tile.dataset.offerTier);
      tile.classList.toggle('is-active', tier === state.count || (tier === 5 && state.count >= 5));
    });
    this.setText('[data-progress]', this.requireServerQuote && this.market.country !== 'ES' ? this.labels.progressAutomatic || '' : this.getProgressText(state));
    const quoteFailed = this.requireServerQuote && (this.serverQuote?.status === 'error' || this.serverOffersStatus === 'error');
    this.setText('[data-quote-status]', quoteFailed ? this.labels.quoteError || this.labels.pendingNote : this.requireServerQuote && state.count > 0 && !state.quoteReady ? this.labels.quoteLoading || this.labels.pending : '');
    this.querySelectorAll('[data-quote-retry]').forEach((button) => { button.hidden = !quoteFailed; });
    this.setText('[data-shipping]', this.getShippingText(state));
    for (let count = 1; count <= 5; count += 1) {
      const total = count === 1 ? getIndividualOfferPrice(this.quantities, this.market, this.catalog) : this.requireServerQuote && !this.hasServerOffers ? null : this.market.prices[count];
      this.setText(`[data-offer-price="${count}"]`, validAmount(total) ? this.formatMoney(total) : this.labels.pending);
      this.setText(`[data-offer-unit-price="${count}"]`, validAmount(total) ? this.formatMoney(total / count) : this.labels.pending);
    }
    this.querySelectorAll('[data-offer-price-prefix="1"]').forEach((node) => {
      node.textContent = this.labels.from;
      node.hidden = state.count === 1 || !hasVariableUnitPrices(this.catalog);
    });
    this.querySelectorAll('[data-offer-from]').forEach((node) => {
      node.textContent = this.labels.from;
      node.hidden = !this.serverOffersMarkFrom;
    });
    const fiveTotal = this.requireServerQuote && !this.hasServerOffers ? null : this.market.prices[5];
    this.setText('[data-five-example]', (this.labels.fiveExample || '{total}').replace('{total}', validAmount(fiveTotal) ? this.formatMoney(fiveTotal) : this.labels.pending));
    this.querySelectorAll('[data-savings]').forEach((node) => {
      node.hidden = state.savings === null || state.savings === 0;
      node.textContent = state.savings ? `${this.labels.savings} ${this.formatMoney(state.savings)}` : '';
    });
    this.querySelectorAll('[data-clear-selection], [data-preview-checkout]').forEach((node) => { node.disabled = cartBusy || state.count === 0; });
    this.catalog.forEach((item) => {
      const quantity = this.quantities[String(item.id)] || 0;
      const nodes = this.cards.get(String(item.id));
      const stock = availableStock(item);
      const unavailable = stock === 0;
      nodes.card.classList.toggle('is-selected', quantity > 0);
      nodes.card.classList.toggle('is-unavailable', unavailable);
      nodes.photo.setAttribute('aria-pressed', String(quantity > 0));
      nodes.photo.setAttribute('aria-label', `${quantity ? this.labels.deselect : this.labels.select}: ${item.title}${quantity ? ` (${quantity})` : ''}`);
      nodes.photo.disabled = cartBusy || (unavailable || state.count >= MAX_SAFE_QUANTITY) && quantity === 0;
      nodes.count.textContent = String(quantity);
      nodes.minus.disabled = cartBusy || quantity === 0;
      nodes.plus.disabled = cartBusy || unavailable || quantity >= stock || state.count >= MAX_SAFE_QUANTITY;
      nodes.status.hidden = !unavailable;
      nodes.status.textContent = unavailable ? this.labels.unavailable : '';
    });
    const list = this.querySelector('[data-selection-list]');
    if (list) this.renderSelectionList(list);
    if (this.previewDialog?.open) this.renderPreviewContents();
    this.dispatchEvent(new CustomEvent('lups:selection-change', { detail: { ...state, quantities: { ...this.quantities }, preview: true }, bubbles: true }));
  }

  renderSelectionList(target) {
    const fragment = document.createDocumentFragment();
    let count = 0;
    this.catalog.forEach((item) => {
      const quantity = this.quantities[String(item.id)] || 0;
      if (!quantity) return;
      count += quantity;
      const row = document.createElement(target.tagName === 'UL' || target.tagName === 'OL' ? 'li' : 'div');
      row.className = 'lups-picked-item';
      const image = document.createElement('img');
      image.src = item.image || '';
      image.alt = '';
      image.width = 56;
      image.height = 56;
      const text = document.createElement('span');
      text.textContent = `${item.title} × ${quantity}`;
      row.append(image, text);
      fragment.append(row);
    });
    if (!count) {
      const empty = document.createElement(target.tagName === 'UL' || target.tagName === 'OL' ? 'li' : 'p');
      empty.className = 'lups-selection-empty';
      empty.textContent = this.labels.emptySelection;
      fragment.append(empty);
    }
    target.replaceChildren(fragment);
  }

  createDialog() {
    this.previewDialog = document.createElement('dialog');
    this.previewDialog.className = 'lups-preview-dialog';
    const title = document.createElement('h2');
    title.id = `lups-preview-title-${Math.random().toString(36).slice(2, 9)}`;
    title.textContent = this.labels.previewTitle;
    this.previewDialog.setAttribute('aria-labelledby', title.id);
    const note = document.createElement('p');
    note.className = 'lups-preview-note';
    note.textContent = this.labels.previewNote;
    this.previewItems = document.createElement('ul');
    this.previewItems.className = 'lups-preview-items';
    this.previewPrice = document.createElement('p');
    this.previewPrice.className = 'lups-preview-price';
    this.previewPending = document.createElement('p');
    this.previewPending.textContent = this.labels.pendingNote;
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'button button--primary';
    close.textContent = this.labels.close;
    close.addEventListener('click', () => this.previewDialog.close());
    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'button button--secondary';
    clear.textContent = this.labels.clear;
    clear.addEventListener('click', () => { this.clearSelection(); this.previewDialog.close(); });
    const actions = document.createElement('div');
    actions.className = 'lups-preview-actions';
    actions.append(close, clear);
    this.previewDialog.append(title, note, this.previewItems, this.previewPrice, this.previewPending, actions);
    this.previewDialog.addEventListener('click', (event) => {
      if (event.target !== this.previewDialog) return;
      const bounds = this.previewDialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.previewDialog.close();
    });
    this.append(this.previewDialog);
  }

  renderPreviewContents() {
    const state = this.displaySelection();
    this.renderSelectionList(this.previewItems);
    this.previewPrice.textContent = `${this.labels.total}: ${state.total === null ? this.labels.pending : this.formatMoney(state.total)}`;
    this.previewPending.hidden = state.total !== null;
  }

  openPreview() {
    if (!calculateSelection(this.quantities, this.market, this.catalog).count) return;
    this.renderPreviewContents();
    this.previewDialog.showModal();
  }
}

if (globalThis.customElements && !customElements.get('lups-color-selector')) customElements.define('lups-color-selector', LupsColorSelector);
